import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Overline, Chapter } from "../components/Reveal";

const VALUES = [
    { t: "Relevance Over Volume", c: "Three considered opportunities beat three hundred listings." },
    { t: "Context Before Commitment", c: "Every project we present arrives with its location, pricing and developer context attached." },
    { t: "Relationships Over Transactions", c: "With buyers, developers and partners alike, we build for repeat business — not one-off transactions." },
];

const About = () => (
    <main data-testid="about-page">
        <section className="bg-charcoal pb-20 pt-40 text-ivory md:pb-28 md:pt-52">
            <div className="mx-auto max-w-[1400px] px-6 md:px-12">
                <Reveal>
                    <Overline>About LUXATI</Overline>
                    <h1 className="mt-6 max-w-4xl font-serif text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl">
                        A More Considered Approach to <span className="italic text-gold">Real Estate.</span>
                    </h1>
                </Reveal>
            </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-36">
            <Chapter number="01" label="Who We Are" />
            <div className="mt-12 grid gap-12 md:grid-cols-12">
                <Reveal className="md:col-span-6">
                    <p className="text-base font-light leading-relaxed text-ink/75 md:text-lg">
                        LUXATI was created around a simple idea: property decisions should begin with
                        understanding, not inventory.
                    </p>
                    <p className="mt-6 text-base font-light leading-relaxed text-ink/70">
                        LUXATI is the real estate investment and advisory vertical of Ibizo Technologies
                        Private Limited.
                    </p>
                    <p className="mt-6 text-base font-light leading-relaxed text-ink/70">
                        We work across buyers, investors, developers, sales teams and real estate partners
                        to help relevant opportunities reach the right conversations.
                    </p>
                </Reveal>
                <Reveal delay={0.15} className="md:col-span-5 md:col-start-8">
                    <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold">Current Focus</p>
                    <ul className="mt-5 space-y-4 text-base font-light leading-relaxed text-ink/70">
                        <li className="border-b border-ink/10 pb-4">
                            <span className="font-normal text-ink">India</span> — primarily Delhi NCR and
                            selected Indian markets.
                        </li>
                        <li className="border-b border-ink/10 pb-4">
                            <span className="font-normal text-ink">Dubai</span> — selected residential and
                            investment opportunities.
                        </li>
                        <li>
                            <span className="font-normal text-ink">International</span> — further markets may
                            be introduced selectively as the network develops.
                        </li>
                    </ul>
                    <div className="mt-10 border-l-2 border-gold pl-6">
                        <p className="font-serif text-xl font-light italic leading-relaxed text-ink/80">
                            "Understand. Discover. Evaluate. Connect."
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>

        <section className="border-t border-ink/10 bg-white/50">
            <div className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-32">
                <Chapter number="02" label="How We Think" />
                <div className="mt-16 grid gap-12 md:grid-cols-3">
                    {VALUES.map((v, i) => (
                        <Reveal key={v.t} delay={i * 0.12} className="border-t border-ink/15 pt-8">
                            <p className="font-serif text-sm italic text-gold">{String(i + 1).padStart(2, "0")}</p>
                            <h3 className="mt-3 font-serif text-2xl font-medium tracking-tight text-ink">{v.t}</h3>
                            <p className="mt-4 text-sm font-light leading-relaxed text-ink/65">{v.c}</p>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-32">
            <Chapter number="03" label="Begin" />
            <Reveal className="mt-10 max-w-2xl">
                <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl">
                    Start With a <span className="italic text-gold">Conversation.</span>
                </h2>
                <p className="mt-8 text-base font-light leading-relaxed text-ink/70">
                    Tell us your objective, budget and timeline. We'll start with the opportunities that
                    make sense for you.
                </p>
                <Link
                    to="/contact"
                    data-testid="about-contact-cta"
                    className="group mt-10 inline-flex items-center gap-3 bg-ink px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-ink"
                >
                    Begin a Conversation
                    <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                </Link>
            </Reveal>
        </section>
    </main>
);

export default About;
